import React, { useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Info, Search } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "components/ui/dialog";
import { Checkbox } from "components/ui/checkbox";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "components/ui/tooltip";
import { useUserInfo } from "../../../../context/user";
import { useAgentsList } from "../../../../queries/agents";

const formatFileSize = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Iconify's CDN (no package install needed) — "vscode-icons" set for
// recognizable, colored file-type glyphs.
const FILE_ICON_BY_EXT = {
  pdf: "file-type-pdf2",
  doc: "file-type-word",
  docx: "file-type-word",
  xls: "file-type-excel",
  xlsx: "file-type-excel",
  csv: "file-type-csv",
  ppt: "file-type-powerpoint",
  pptx: "file-type-powerpoint",
  txt: "file-type-text",
  md: "file-type-markdown",
  json: "file-type-json",
  mp3: "file-type-audio",
  wav: "file-type-audio",
  m4a: "file-type-audio",
  flac: "file-type-audio",
  mp4: "file-type-video",
  mpeg: "file-type-video",
  webm: "file-type-video",
  mov: "file-type-video",
  zip: "file-type-zip",
  rar: "file-type-zip",
  jpg: "file-type-image",
  jpeg: "file-type-image",
  png: "file-type-image",
  gif: "file-type-image",
  webp: "file-type-image",
  svg: "file-type-image",
};

const getFileIconUrl = (fileName) => {
  const ext = (fileName || "").split(".").pop()?.toLowerCase();
  const icon = FILE_ICON_BY_EXT[ext] || "default-file";
  return `https://api.iconify.design/vscode-icons/${icon}.svg`;
};

// ponytail: character-count heuristic, not a real pixel measurement — fine
// for this modal's fixed width; swap for canvas-measured truncation if the
// row ever gets a flexible width.
const truncateMiddle = (name, maxLength = 32) => {
  if (!name || name.length <= maxLength) return name;
  const dot = name.lastIndexOf(".");
  const ext = dot > 0 ? name.slice(dot) : "";
  const base = dot > 0 ? name.slice(0, dot) : name;
  const keep = Math.max(maxLength - ext.length - 3, 4);
  const head = Math.ceil(keep / 2);
  const tail = Math.floor(keep / 2);
  return `${base.slice(0, head)}...${base.slice(base.length - tail)}${ext}`;
};

/**
 * Shown instead of attaching a file directly to the chat/Task composer when
 * it exceeds MAX_CHAT_FILE_BYTES (uploadFiles/index.js) — the composer has no
 * room for a 50MB+ file, but the Knowledge Base dropzone already accepts up
 * to 512MB (components/uploadFiles/MyDropzone.jsx), so this redirects the
 * file there instead of just rejecting it. Matches the Lovable reference
 * design for this flow.
 */
const FileTooBigModal = ({
  file,
  open,
  onOpenChange,
  onFileSelect,
  onRunNow,
  onAutomate,
}) => {
  // Index 16 is the Knowledge Base uploader (chunked, no chat-composer size
  // cap, wires up assistant_ids) — NOT usersContext.js's same-named chat
  // uploader, which rejected every file here outright (CU-123yxuaumrv).
  const [userInfo, , , , userActiveLocation, , , , , , , , , , , , contextuploadFilefrominput] = useUserInfo();
  const { data: agentsFetchData } = useAgentsList(
    userInfo?._id,
    userActiveLocation?.id,
  );
  const [selectedAgentIds, setSelectedAgentIds] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [addedToKnowledgeBase, setAddedToKnowledgeBase] = useState(false);
  const [agentSearch, setAgentSearch] = useState("");

  const agents = useMemo(
    () => (agentsFetchData?.data || []).filter((item) => item.status),
    [agentsFetchData],
  );
  const filteredAgents = useMemo(
    () =>
      agents.filter((agent) =>
        (agent.assistant_name || "")
          .toLowerCase()
          .includes(agentSearch.trim().toLowerCase()),
      ),
    [agents, agentSearch],
  );
  const allSelected = agents.length > 0 && selectedAgentIds.length === agents.length;

  const toggleAgent = (assistantId) => {
    setSelectedAgentIds((prev) =>
      prev.includes(assistantId)
        ? prev.filter((id) => id !== assistantId)
        : [...prev, assistantId],
    );
  };

  const toggleAll = () => {
    setSelectedAgentIds(allSelected ? [] : agents.map((a) => a.assistant_id));
  };

  const handleAddToKnowledgeBase = async () => {
    if (!file || selectedAgentIds.length === 0 || uploading) return;
    setUploading(true);
    try {
      const response = await contextuploadFilefrominput(
        [file],
        userActiveLocation?.id,
        userInfo?.userName,
        selectedAgentIds.map((id) => ({ value: id })),
        "knowledge_base",
      );
      if (response?.data?.data?.[0]?.success) {
        toast.success(`${file.name} added to Knowledge Base`);
        setAddedToKnowledgeBase(true);
        // Attach it to the chat composer too — a plain object (not a File
        // instance) so ChatInput's addFilesWithUpload adds it straight to
        // selectedFiles without re-uploading it through the composer's
        // capped uploader (CU-123yxuaumrv).
        onFileSelect?.([
          {
            id: response.data.data[0]?.file_id,
            name: file.name,
            size: file.size,
            type: file.type,
          },
        ]);
      } else {
        toast.error(
          response?.data?.data?.[0]?.error ||
            response?.data?.message ||
            "Failed to add file to Knowledge Base.",
        );
      }
    } catch (err) {
      console.error("Failed to add oversized file to Knowledge Base:", err);
      toast.error("Failed to add file to Knowledge Base.");
    } finally {
      setUploading(false);
    }
  };

  const resetAndClose = () => {
    setSelectedAgentIds([]);
    setAddedToKnowledgeBase(false);
    setAgentSearch("");
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) resetAndClose();
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <span className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-medium">
            File too big
          </span>
          <DialogTitle>This file is too big for the chat</DialogTitle>
          <DialogDescription>
            Add it to your Knowledge Base from here and your Agents can use it.
          </DialogDescription>
        </DialogHeader>

        {file && (
          <div className="flex items-center gap-3 rounded-md border border-border bg-muted/40 px-3 py-2.5 text-sm">
            <img
              src={getFileIconUrl(file.name)}
              alt=""
              className="h-8 w-8 shrink-0"
            />
            <div className="flex min-w-0 flex-col">
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="truncate font-medium text-foreground">
                      {truncateMiddle(file.name)}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top">{file.name}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <span className="text-xs text-muted-foreground">{formatFileSize(file.size)}</span>
            </div>
          </div>
        )}

        <div>
          <p className="mb-2 text-sm font-medium text-foreground">
            Which Agents should use this file?
          </p>
          <div className="relative mb-2">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search Agents"
              value={agentSearch}
              onChange={(e) => setAgentSearch(e.target.value)}
              className="h-9 pl-8 text-sm"
            />
          </div>
          <div className="max-h-48 overflow-y-auto rounded-md border border-border">
            {!agentSearch && (
              <label className="flex items-center gap-2.5 border-b border-border px-3 py-2 text-sm font-medium cursor-pointer hover:bg-muted/60">
                <Checkbox checked={allSelected} onCheckedChange={toggleAll} />
                All Agents
              </label>
            )}
            {filteredAgents.map((agent) => (
              <label
                key={agent.assistant_id}
                className="flex items-center gap-2.5 px-3 py-2 text-sm cursor-pointer hover:bg-muted/60"
              >
                <Checkbox
                  checked={selectedAgentIds.includes(agent.assistant_id)}
                  onCheckedChange={() => toggleAgent(agent.assistant_id)}
                />
                {agent.assistant_name}
              </label>
            ))}
            {filteredAgents.length === 0 && (
              <p className="px-3 py-2 text-sm text-muted-foreground">
                {agentSearch ? `No Agents match "${agentSearch}"` : "No Agents available."}
              </p>
            )}
          </div>
          <p className="mt-1.5 text-xs text-muted-foreground">
            {selectedAgentIds.length === 0
              ? "Select at least one Agent"
              : `${selectedAgentIds.length} Agent${selectedAgentIds.length === 1 ? "" : "s"} selected`}
          </p>
        </div>

        <Button
          type="button"
          className="w-full"
          disabled={selectedAgentIds.length === 0 || uploading}
          onClick={handleAddToKnowledgeBase}
        >
          {uploading
            ? "Adding…"
            : addedToKnowledgeBase
              ? "Added to Knowledge Base"
              : "Add to Knowledge Base"}
        </Button>

        <div className="space-y-2.5 border-t border-border/60 pt-3">
          {!addedToKnowledgeBase && (
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Info className="h-3.5 w-3.5 shrink-0" />
              Select Agents and add this file to your Knowledge Base first.
            </p>
          )}
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              disabled={!addedToKnowledgeBase}
              onClick={() => {
                resetAndClose();
                onRunNow?.();
              }}
            >
              Run this Task now
            </Button>
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              disabled={!addedToKnowledgeBase}
              onClick={() => {
                resetAndClose();
                onAutomate?.();
              }}
            >
              Automate this Task
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FileTooBigModal;
